// ════════════════════════════════════════════════════════════════════
// ieServers.js — WHICH COPY OF THE WORLD AM I IN
// ════════════════════════════════════════════════════════════════════
// IE_SERVERS_VERSION 1.0 — minted 2026-10-16.
//
// MASTER COPY: gamemaster/engine/ieServers.js
// Vendored BYTE-IDENTICAL into every title that has servers (Hooked,
// Terror today). Fix it here first, then re-vendor. Needs ieSocial 1.1+
// (THE SERVER SIGNAL) and ieIdentity beside it.
//
// ────────────────────────────────────────────────────────────────────
// WHAT caP'n ASKED FOR
// ────────────────────────────────────────────────────────────────────
// "if you're partied up and you walk into Hooked, your friend can walk
//  in right after and it will ask if you want open a solo server or
//  join your party's server? Party server taking priority being the
//  'highlighted' choice … or of course you can click on a friend's
//  profile and simply join their server."
//
// ────────────────────────────────────────────────────────────────────
// THE ARRIVAL LAW — where you land, in this order
// ────────────────────────────────────────────────────────────────────
//   1. ?server=X on the url (a friend's JOIN, an invite)  → X. No prompt.
//   1½. back from a screen inside the same title (Terror: a duel, then the
//      Plaza again) → the server you were on. Remembered for the page's
//      life, so a solo server isn't lost to a Solrend.
//   2. partied, and a partymate is live in this game      → THE PROMPT:
//        [ JOIN PARTY'S SERVER ]  ← highlighted, Enter picks it
//        [ OPEN A SOLO SERVER  ]
//   3. anything else (solo, or first of the party in)     → the first
//      PUBLIC server with room: Server 1, else 2, else 3 … (THE FULL LAW).
//      The first partymate in needs no prompt: there is nothing to
//      choose between yet, and wherever they land BECOMES the party's
//      server for the next one through the door.
//
// ⭐ THE FULL LAW (caP'n, 2026-10-16): "we just need our party mates to be
// able to squad up 100% of the time, if the server's full for some reason
// they go to another where it can begin to repopulate".
//   · A public server holds SERVER_CAPS[game] souls (a DIAL per title).
//     ⚠ SOFT, honestly: a seat is only held while the title's server screen
//     is mounted (Terror: the Plaza — someone in a duel doesn't count), a
//     remembered server skips the check, and two strangers checking at the
//     same instant can both take the last spot. "4" means about 4.
//     A stranger arriving to a full one is sent to the next public server.
//   · THE PARTY NEVER BOUNCES. Joining your party's server — the prompt,
//     the chip — ignores the cap. So does a friend's JOIN and a url ticket:
//     anything aimed at a person, not at "anywhere".
//   · Public servers are numbered "1", "2", "3"…; solo servers are "s…".
//
// "Server 1" is the public world — the one every title already had.
// A solo server ("s…") is a fresh empty copy; friends can still JOIN it
// from your profile, strangers never stumble in.
//
// ────────────────────────────────────────────────────────────────────
// THE NODES — no rules deploy needed
// ────────────────────────────────────────────────────────────────────
//   games/{game}/servers/{sid}/seats/{uid}   { name, ts }
//       WHO IS HERE. games/$gameId is the open umbrella (auth != null).
//       onDisconnect-removed; ts is the server's clock; read-side drops
//       anything older than SEAT_STALE_MS.
//   parties/{pid}/members/{uid}              { …, game, server, ts }
//       WHERE MY PARTY IS. Pier's PartySystem already beats `game` and
//       `ts` here; we add `server`. A stale pointer (not really a
//       member) is refused by members/$uid's `name` validate — which is
//       the guard, on purpose.
//   presence/{uid}.url  …?server=X                (ieSocial, see there)
//       WHERE MY FRIENDS CAN FIND ME.
//
// ⚠ KNOWN GAP: the party row beats only while the hook is mounted. In
// Terror that is the Plaza — during a duel your row ages out (MATE_STALE_MS),
// so a partymate arriving mid-duel lands on Server 1, not your server.
//
// ⚑ A title keeps its own per-server world data under its own path
// (Hooked: games/hooked/servers/{sid}/anglers). This file never knows
// what a server CONTAINS, only who is in which one.
//
// ────────────────────────────────────────────────────────────────────
// ⚑ PINNED — THE PARTNER (caP'n, 2026-10-16: "pin the partner update")
// ────────────────────────────────────────────────────────────────────
// "our partner should have this ease of access as well always, they
//  will almost always get what the user gets". The seams are already
// partner-shaped: a seat is a uid + name, the party row is a uid, and
// join(sid) is a plain function. The partner session (Lyraei Plays)
// wires: her uid seated beside her player's (follow their sid), the
// prompt answered by whatever her player picked, and JOIN on her
// profile. NOT built yet — another session.
// ════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ref, get, set, update, remove, onValue, onDisconnect, serverTimestamp } from "firebase/database";
import { db } from "./firebase";
import {
  PUBLIC_SERVER, cleanServerId, setPresenceServer, setServerJoinHandler, serverNow,
} from "./ieSocial";
import { handoffPartyId } from "./ieIdentity";

export const IE_SERVERS_VERSION = "1.0";

// ── DIALS ───────────────────────────────────────────────────────────
export const SEAT_BEAT_MS   = 20000;   // ⟵ DIAL  seat heartbeat (backstop to onDisconnect)
export const SEAT_STALE_MS  = 60000;   // ⟵ DIAL  read-side: a seat older than this is gone
export const MATE_BEAT_MS   = 10000;   // ⟵ DIAL  "where is my party" beat (Pier's own cadence)
export const MATE_STALE_MS  = 45000;   // ⟵ DIAL  a partymate older than this isn't "here"
export const PARTY_WAIT_MS  = 2500;    // ⟵ DIAL  how long arrival waits to hear about the party

// ── PATHS ───────────────────────────────────────────────────────────
export const serverPath = (game, sid)      => `games/${game}/servers/${sid}`;
export const seatsPath  = (game, sid)      => `games/${game}/servers/${sid}/seats`;
export const seatPath   = (game, sid, uid) => `games/${game}/servers/${sid}/seats/${uid}`;

// ── THE ARRIVAL TICKET — read once, at import ────────────────────────
// Read here (not in an effect) for the same reason ieIdentity reads the
// handoff at import: a host app may tidy the address bar first.
function readArrival() {
  if (typeof window === "undefined") return null;
  try { return cleanServerId(new URLSearchParams(window.location.search).get("server")); }
  catch (e) { return null; }
}
let _arrival = readArrival();
export const arrivalServer = () => _arrival;
function spendArrival() {
  _arrival = null;
  if (typeof window === "undefined") return;
  try {
    const p = new URLSearchParams(window.location.search);
    if (!p.has("server")) return;
    p.delete("server");
    const q = p.toString();
    window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : "") + window.location.hash);
  } catch (e) { /* ignore */ }
}

// ── THE MEMORY — the last server per game + soul, for this page's life ─
const _remembered = {};
const _lastByGame = {};   // game → the sid this page stood on last (whoever was signed in)
const memKey = (game, uid) => `${game}:${uid}`;

// The last server this page stood on in `game` (any soul — one player per
// page). Lets a title's OTHER screens follow the server: Terror's rooms put
// their chat on the server's channel with it.
export const lastServer = (game) => _lastByGame[game] || null;
// A channel scoped to a server. Server 1 keeps the bare name, so everything
// that already lives there (Terror's "lobby" chat) is untouched.
export const serverChannel = (base, sid) => (!sid || sid === PUBLIC_SERVER) ? base : `${base}~${sid}`;

// ── THE CAPS (THE FULL LAW) ─────────────────────────────────────────
export const SERVER_CAPS = {
  terror: 4,      // ⟵ DIAL  the Plaza was built for 4 (its "n/4" counter, 3 others drawn)
  hooked: 12,     // ⟵ DIAL
  moonshine: 4,   // ⟵ DIAL  co-op up to 4 (Moonshine PRIME law 2), 2026-10-24
  miniwili: 4,    // ⟵ DIAL  turn-based golf, a foursome (2026-10-25)
};
export const DEFAULT_CAP = 16;      // ⟵ DIAL  any title without a row
export const MAX_PUBLIC  = 50;      // ⟵ DIAL  how far the search for room goes
export const serverCap = (game) => SERVER_CAPS[game] || DEFAULT_CAP;

// How many souls are on a server right now (fresh seats, me excluded).
export async function seatCount(game, sid, myUid) {
  try {
    const snap = await get(ref(db, seatsPath(game, sid)));
    const now = serverNow();
    return Object.entries(snap.val() || {})
      .filter(([u, r]) => u !== myUid && r && r.ts && now - r.ts < SEAT_STALE_MS).length;
  } catch (e) { return 0; }    // can't read → assume room; never strand anyone
}
// The first public server with room — room for `need` souls (the whole
// party, when the first of a party walks in, so the rest never push a
// server past its size; caP'n: "4 is the only number for Terror party
// size", the Plaza is physically built for 4). Server 1 first, always. Never hangs:
// offline, a get() can wait forever, so after PICK_WAIT_MS it's Server 1.
const PICK_WAIT_MS = 3000;   // ⟵ DIAL
export function pickPublicServer(game, myUid, need = 1) {
  return Promise.race([
    searchPublic(game, myUid, need),
    new Promise((res) => setTimeout(() => res(PUBLIC_SERVER), PICK_WAIT_MS)),
  ]);
}
async function searchPublic(game, myUid, need) {
  const cap = serverCap(game);
  const want = Math.max(1, Math.min(need || 1, cap));
  for (let n = 1; n <= MAX_PUBLIC; n++) {
    if ((await seatCount(game, String(n), myUid)) + want <= cap) return String(n);
  }
  return String(MAX_PUBLIC + 1);
}

// ── NAMES ───────────────────────────────────────────────────────────
export const isPublicServer = (sid) => !sid || /^[0-9]+$/.test(sid);
export const isSoloServer   = (sid) => !!sid && sid[0] === "s";
export const newSoloServer  = () =>
  "s" + Date.now().toString(36).slice(-5) + Math.random().toString(36).slice(2, 6);
export function serverLabel(sid) {
  if (!sid) return "…";
  if (isSoloServer(sid)) return "Solo server";
  return `Server ${sid}`;
}

// ════════════════════════════════════════════════════════════════════
// useMyParty — the party I'm really in, or null
// ════════════════════════════════════════════════════════════════════
// The pointer (profiles/{uid}/currentPartyId) is the truth Pier keeps.
// A handoff ?partyId= is honoured only when the pointer is empty, and
// only if I'm actually on the member list — no gatecrashing.
export function useMyParty(uid) {
  // ⚠ STATE KEYED TO ITS INPUT (review, 2026-10-16). The first cut held a
  // bare pointer/party in state. Before going live they were null (= "no
  // party, ready"), and on the very render `uid` arrived they were STILL
  // null — so the arrival law read "ready, no party", sent everyone to
  // Server 1, and the party prompt could never appear. Each value now
  // remembers which uid / pid it belongs to; a value for a different input
  // reads as "not heard yet".
  const [ptr, setPtr] = useState({ uid: null, v: undefined });
  const [pst, setPst] = useState({ pid: null, v: undefined });
  useEffect(() => {
    if (!uid) return undefined;
    return onValue(ref(db, `profiles/${uid}/currentPartyId`),
      (s) => setPtr({ uid, v: s.val() || null }), () => setPtr({ uid, v: null }));
  }, [uid]);
  const pointer = !uid ? null : (ptr.uid === uid ? ptr.v : undefined);
  const pid = pointer === undefined ? undefined : (pointer || handoffPartyId || null);
  useEffect(() => {
    if (!pid || !uid) return undefined;
    return onValue(ref(db, `parties/${pid}`), (s) => {
      const v = s.val();
      setPst({ pid, v: v && v.members && v.members[uid] ? { id: pid, ...v } : null });
    }, () => setPst({ pid, v: null }));
  }, [pid, uid]);
  const party = !uid ? null : pid === undefined ? undefined : !pid ? null : (pst.pid === pid ? pst.v : undefined);
  return { ready: party !== undefined, party: party || null, partyId: party ? party.id : null };
}

// Where the party is standing in `game`, right now. The leader wins a
// tie (the party follows its leader); otherwise whoever beat last.
export function partyServerIn(party, game, myUid) {
  if (!party || !party.members) return null;
  const now = serverNow();
  const here = Object.entries(party.members)
    .filter(([u, m]) => u !== myUid && m && m.game === game && m.server && m.ts && now - m.ts < MATE_STALE_MS)
    .map(([u, m]) => ({ uid: u, name: m.name || "Partymate", sid: cleanServerId(m.server), ts: m.ts }))
    .filter((m) => m.sid);
  if (!here.length) return null;
  const lead = here.find((m) => m.uid === party.leader);
  const pick = lead || here.sort((a, b) => b.ts - a.ts)[0];
  return { sid: pick.sid, mates: here.filter((m) => m.sid === pick.sid).map((m) => m.name) };
}

// ════════════════════════════════════════════════════════════════════
// useServer — the one hook a title mounts
// ════════════════════════════════════════════════════════════════════
// Mount it where uid + name are known. `enabled` false = offline: no
// seat, no prompt, sid stays null (a title runs its solo self).
//
// Returns:
//   sid            the server I'm on (null until seated)
//   phase          "off" | "deciding" | "choosing" | "seated"
//   choice         { sid, mates[] } while the prompt is up
//   seats          [{ uid, name }] live, me included
//   partyServer    { sid, mates[] } | null — where my party is now
//   partyId, party   (party: the parties/{pid} node, members included)
//   join(sid)      move to a server (in place)
//   openSolo()     a fresh solo server
//   goPublic()     back to the public world (first public server with room)
//   choose("party"|"solo")   answer the prompt
export function useServer({ game, uid, name, enabled = true }) {
  const live = !!(enabled && uid && game);
  const { ready: partyReady, party, partyId } = useMyParty(live ? uid : null);
  const [sid, setSid] = useState(null);
  const [phase, setPhase] = useState("off");
  const [choice, setChoice] = useState(null);
  const decidedFor = useRef(null);
  const moveSeq = useRef(0);   // bumps on every move; a slow public search that lost the race is dropped

  const partyServer = useMemo(() => partyServerIn(party, game, uid), [party, game, uid]);

  // ── THE ARRIVAL LAW ───────────────────────────────────────────────
  useEffect(() => {
    // moveSeq bumps here too: a public search still in flight when the hook
    // goes offline (log out, onboarding) must not seat anyone on its return.
    if (!live) { decidedFor.current = null; moveSeq.current++; setSid(null); setPhase("off"); setChoice(null); return undefined; }
    if (decidedFor.current === uid) return undefined;
    // 1. a ticket on the url
    const ticket = arrivalServer();
    if (ticket) {
      decidedFor.current = uid; spendArrival();
      setSid(ticket); setPhase("seated"); return undefined;
    }
    // 1½. back from another screen of this same title
    const mem = _remembered[memKey(game, uid)];
    if (mem) { decidedFor.current = uid; setSid(mem); setPhase("seated"); return undefined; }
    setSid(null);            // a seat from a previous soul/session never carries over
    setPhase("deciding");
    const settle = () => {
      if (decidedFor.current === uid) return;
      decidedFor.current = uid;
      const ps = partyServerIn(party, game, uid);
      if (ps) { setChoice(ps); setPhase("choosing"); return; }   // 2. the prompt
      const seq = ++moveSeq.current;                             // 3. a public server with room
      // first of the party in? find room for the whole party (THE FULL LAW)
      const need = party && party.members ? Object.keys(party.members).length : 1;
      pickPublicServer(game, uid, need).then((pub) => {
        if (moveSeq.current !== seq) return;
        setSid(pub); setPhase("seated");
      });
    };
    if (partyReady) { settle(); return undefined; }
    const t = setTimeout(settle, PARTY_WAIT_MS);           // never hang on a slow party read
    return () => clearTimeout(t);
  }, [live, uid, game, partyReady, party]);

  const join = useCallback((next) => {
    const id = cleanServerId(next);
    if (!id) return false;
    decidedFor.current = uid;
    moveSeq.current++;
    setChoice(null); setSid(id); setPhase("seated");
    return true;
  }, [uid]);
  const openSolo = useCallback(() => join(newSoloServer()), [join]);
  // Back to the public world — through THE FULL LAW, like any stranger.
  const goPublic = useCallback(() => {
    const seq = ++moveSeq.current;
    const need = party && party.members ? Object.keys(party.members).length : 1;
    pickPublicServer(game, uid, need).then((pub) => { if (moveSeq.current === seq) join(pub); });
  }, [game, uid, join, party]);
  const choose = useCallback((which) => {
    if (which === "party" && choice) return join(choice.sid);
    return openSolo();
  }, [choice, join, openSolo]);

  useEffect(() => { if (live && sid) { _remembered[memKey(game, uid)] = sid; _lastByGame[game] = sid; } }, [live, sid, game, uid]);

  // ── THE SEAT ──────────────────────────────────────────────────────
  // Same ordering law as useIEPresence: onDisconnect armed, THEN the row.
  useEffect(() => {
    if (!live || !sid) return undefined;
    const r = ref(db, seatPath(game, sid, uid));
    let off = false;
    const beat = () => { if (!off) set(r, { name: String(name || "Unclaimed").slice(0, 24), ts: serverTimestamp() }).catch(() => {}); };
    const arm = () => beat();
    onDisconnect(r).remove().then(arm).catch(arm);
    const t = setInterval(beat, SEAT_BEAT_MS);
    return () => {
      off = true; clearInterval(t);
      onDisconnect(r).cancel().catch(() => {});
      remove(r).catch(() => {});
    };
  }, [live, game, sid, uid, name]);

  // ── WHO ELSE IS HERE ──────────────────────────────────────────────
  const [seatRows, setSeatRows] = useState({});
  useEffect(() => {
    if (!live || !sid) { setSeatRows({}); return undefined; }
    return onValue(ref(db, seatsPath(game, sid)), (s) => setSeatRows(s.val() || {}), () => setSeatRows({}));
  }, [live, game, sid]);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, [live]);
  const seats = useMemo(() => {
    const now = serverNow();
    return Object.entries(seatRows)
      .filter(([u, r]) => r && (u === uid || (r.ts && now - r.ts < SEAT_STALE_MS)))
      .map(([u, r]) => ({ uid: u, name: r.name || "Unclaimed" }));
  }, [seatRows, uid, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── TELL MY PARTY, TELL MY FRIENDS ────────────────────────────────
  useEffect(() => {
    if (!live || !sid || !partyId) return undefined;
    const r = ref(db, `parties/${partyId}/members/${uid}`);
    // the server's clock, like every other ts in the Empire's social layer —
    // two partymates whose PCs disagreed by ~35s never saw the prompt
    const beat = () => update(r, { game, server: sid, ts: serverTimestamp() }).catch(() => {});
    beat();
    onDisconnect(ref(db, `parties/${partyId}/members/${uid}/server`)).remove().catch(() => {});
    const t = setInterval(beat, MATE_BEAT_MS);
    // ⚠ NO "clear my server" ON CLEANUP. It was written that way first and
    // it races: switching servers runs this cleanup and the next beat
    // back to back, and the async clear could land AFTER the new beat —
    // telling the party I'm nowhere while I'm standing on the new server.
    // Leaving is covered by the beat going stale (MATE_STALE_MS), by
    // onDisconnect above, and by Pier's own beat rewriting `game`.
    return () => clearInterval(t);
  }, [live, sid, partyId, uid, game]);

  useEffect(() => {
    if (!live || !sid) return undefined;
    setPresenceServer(sid);
    return () => setPresenceServer(null);
  }, [live, sid]);

  // A friend's JOIN on another server of THIS game swaps in place.
  useEffect(() => {
    if (!live) return undefined;
    setServerJoinHandler((next) => join(next));
    return () => setServerJoinHandler(null);
  }, [live, join]);

  return { sid, phase, choice, seats, partyServer, partyId, party, join, openSolo, goPublic, choose };
}

// ════════════════════════════════════════════════════════════════════
// THE UI — the prompt and the chip
// ════════════════════════════════════════════════════════════════════
// Self-styled, one `theme` knob per title (Hooked is lantern gold,
// Terror is violet). Inline styles only — no CSS file to vendor.
const DEF = {
  accent: "#a78bfa", ink: "#ece6f5", dim: "#a49cb4", bg: "#0c0a12", panel: "#16121f",
  font: "system-ui, sans-serif", display: "Georgia, serif", z: 900,
};

export function ServerChoice({ server, theme }) {
  const T = { ...DEF, ...(theme || {}) };
  const open = server && server.phase === "choosing" && server.choice;
  const { choose } = server || {};
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => {
      if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); choose("party"); }
    };
    window.addEventListener("keydown", k, true);
    return () => window.removeEventListener("keydown", k, true);
  }, [open, choose]);
  if (!open) return null;
  const mates = server.choice.mates || [];
  const who = mates.length === 0 ? "Your party" : mates.length === 1 ? mates[0] : `${mates[0]} +${mates.length - 1}`;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: T.z, display: "grid", placeItems: "center",
      background: "rgba(0,0,0,0.55)", backdropFilter: "blur(3px)" }}>
      <div role="dialog" aria-label="Pick a server" style={{
        width: "min(360px, calc(100vw - 32px))", padding: "22px 20px 18px", borderRadius: 16,
        background: `linear-gradient(180deg, ${T.panel}, ${T.bg})`, border: `1px solid ${T.accent}55`,
        boxShadow: `0 18px 60px rgba(0,0,0,0.7), 0 0 30px ${T.accent}22`, textAlign: "center", color: T.ink,
      }}>
        <div style={{ fontFamily: T.display, fontSize: 20, letterSpacing: 1, color: T.ink }}>Your party's here</div>
        <div style={{ fontFamily: T.font, fontSize: 12.5, color: T.dim, marginTop: 6 }}>
          {who} {mates.length > 1 ? "are" : "is"} on {serverLabel(server.choice.sid)}.
        </div>
        <button autoFocus onClick={() => choose("party")} style={{
          display: "block", width: "100%", marginTop: 18, padding: "12px 14px", borderRadius: 11, cursor: "pointer",
          fontFamily: T.font, fontSize: 13, fontWeight: 700, letterSpacing: 1.6, color: T.bg,
          background: T.accent, border: `1px solid ${T.accent}`, boxShadow: `0 0 18px ${T.accent}66`,
        }}>JOIN PARTY'S SERVER</button>
        <button onClick={() => choose("solo")} style={{
          display: "block", width: "100%", marginTop: 9, padding: "10px 14px", borderRadius: 11, cursor: "pointer",
          fontFamily: T.font, fontSize: 12, letterSpacing: 1.4, color: T.dim,
          background: "transparent", border: `1px solid ${T.dim}44`,
        }}>OPEN A SOLO SERVER</button>
        <div style={{ fontFamily: T.font, fontSize: 10, color: T.dim, opacity: 0.7, marginTop: 10, letterSpacing: 1.2 }}>ENTER JOINS YOUR PARTY</div>
      </div>
    </div>
  );
}

// The chip: where am I, how many are here, and the three moves.
export function ServerChip({ server, theme, style }) {
  const T = { ...DEF, ...(theme || {}) };
  const [open, setOpen] = useState(false);
  if (!server || server.phase !== "seated" || !server.sid) return null;
  const { sid, seats, partyServer } = server;
  const partyHere = partyServer && partyServer.sid === sid;
  const row = (label, onClick, on) => (
    <button key={label} onClick={() => { setOpen(false); onClick(); }} style={{
      display: "block", width: "100%", textAlign: "left", padding: "8px 11px", cursor: "pointer",
      fontFamily: T.font, fontSize: 11.5, letterSpacing: 0.6, color: on ? T.accent : T.ink,
      background: "transparent", border: "none", borderTop: `1px solid ${T.dim}22`,
    }}>{label}</button>
  );
  return (
    <div style={{ position: "fixed", zIndex: T.z - 10, ...(style || {}) }}>
      <button onClick={() => setOpen((o) => !o)} title="Servers" style={{
        padding: "6px 11px", borderRadius: 999, cursor: "pointer", whiteSpace: "nowrap",
        fontFamily: T.font, fontSize: 10, letterSpacing: 1.6, color: T.ink,
        background: `linear-gradient(180deg, ${T.panel}, ${T.bg})`, border: `1px solid ${T.accent}55`,
        boxShadow: "0 2px 10px rgba(0,0,0,0.6)",
      }}>
        <span style={{ color: T.accent }}>{partyHere ? "♦" : "◈"}</span> {serverLabel(sid).toUpperCase()}
        <span style={{ color: T.dim }}> · {Math.max(1, seats.length)}</span>
      </button>
      {open && (
        <div style={{ marginTop: 6, minWidth: 200, borderRadius: 11, overflow: "hidden",
          background: T.panel, border: `1px solid ${T.accent}44`, boxShadow: "0 10px 30px rgba(0,0,0,0.7)" }}>
          <div style={{ padding: "8px 11px", fontFamily: T.font, fontSize: 10, letterSpacing: 1.4, color: T.dim }}>
            {seats.length <= 1 ? "Just you here" : `${seats.length} here: ${seats.slice(0, 4).map((s) => s.name).join(", ")}${seats.length > 4 ? "…" : ""}`}
          </div>
          {partyServer && !partyHere && row(`Join party's server (${partyServer.mates[0] || "party"})`, () => server.join(partyServer.sid), true)}
          {!isPublicServer(sid) && row("Back to a public server", server.goPublic)}
          {row(isSoloServer(sid) ? "Open a fresh solo server" : "Open a solo server", server.openSolo)}
        </div>
      )}
    </div>
  );
}
